import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { UserEntity, selectUserBaseSyncBeforeQuery } from '@zerops/zerops/core/user-base';
import {
  onWebsocketMessageDispatchAddRemoveEntities,
  onWebsocketMessageDispatchUpdateEntities
} from '@zerops/zef/entities';
import { map, mergeMap, switchMap, catchError, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { ClientUserEntity } from './client-user.entity';
import { ClientUserBaseApi } from './client-user-base.api';
import { clientUserActions } from './client-user-base.state';

@Injectable()
export class ClientUserBaseEffect {
  #userEntity = inject(UserEntity);
  #clientUserEntity = inject(ClientUserEntity);
  #actions$ = inject(Actions);
  #api = inject(ClientUserBaseApi);
  #store = inject(Store);

  // `clientUser` search is served asynchronously, so right after the session's
  // identity changed server-side (ZCP pool claim on registration) it can still
  // answer with the pre-claim userId — ask the API to sync before querying.
  #syncBeforeQuery$ = this.#store.pipe(select(selectUserBaseSyncBeforeQuery));

  // # Client user entity streams
  onClientIdSetupListStream$ = createEffect(() => this.#userEntity.activeClientId$.pipe(
    withLatestFrom(this.#syncBeforeQuery$),
    map(([ clientId, syncBeforeQuery ]) => this.#clientUserEntity.listSubscribe(
      clientId,
      '',
      { syncBeforeQuery }
    ))
  ));

  onClientUserListChange$ = createEffect(() => this.#actions$.pipe(
    onWebsocketMessageDispatchAddRemoveEntities(this.#clientUserEntity)
  ));

  onClientIdSetupUpdateStream$ = createEffect(() => this.#userEntity.activeClientId$.pipe(
    withLatestFrom(this.#syncBeforeQuery$),
    map(([ clientId, syncBeforeQuery ]) => this.#clientUserEntity.updateSubscribe(
      clientId,
      { syncBeforeQuery }
    ))
  ));

  onClientUserUpdate$ = createEffect(() => this.#actions$.pipe(
    onWebsocketMessageDispatchUpdateEntities(this.#clientUserEntity)
  ));

  // # Create user (POST /api/client/{clientId}/user)
  onCreateUser$ = createEffect(() => this.#actions$.pipe(
    ofType(clientUserActions.createUser),
    switchMap((action) =>
      this.#api.createUser$(action.data.clientId, action.data.payload).pipe(
        mergeMap((response) => [
          clientUserActions.createUserSuccess({ response }, action),
          this.#clientUserEntity.addToCache([ response ])
        ]),
        catchError((error) => of(
          clientUserActions.createUserFail(error, action)
        ))
      )
    )
  ));

  // # Project roles management
  onLoadProjectRoles$ = createEffect(() => this.#actions$.pipe(
    ofType(clientUserActions.loadProjectRoles),
    switchMap((action) =>
      this.#api.getUserProjectRoles(action.data.userId).pipe(
        map((response) =>
          clientUserActions.loadProjectRolesSuccess(
            { userId: action.data.userId, response },
            action
          )
        ),
        catchError((error) => of(
          clientUserActions.loadProjectRolesFail(error, action)
        ))
      )
    )
  ));

  onUpdateProjectRoles$ = createEffect(() => this.#actions$.pipe(
    ofType(clientUserActions.updateProjectRoles),
    switchMap((action) =>
      this.#api.updateUserProjectRoles(action.data.userId, action.data.payload).pipe(
        map((response) =>
          clientUserActions.updateProjectRolesSuccess(
            { userId: action.data.userId, response },
            action
          )
        ),
        catchError((error) => of(
          clientUserActions.updateProjectRolesFail(error, action)
        ))
      )
    )
  ));
}
